import React, { useEffect, useRef, useState } from 'react';
import { X, Sparkles } from 'lucide-react';

interface CoachMarkProps {
  /** Unique id — used as the localStorage key for dismissal. */
  id: string;
  /** Ref to the DOM element the mark should point at. */
  anchorRef: { readonly current: HTMLElement | null };
  /** Main headline of the hint. */
  title: string;
  /** One or two sentence body. */
  body: string;
  /** Where to place the bubble relative to the anchor. Default 'bottom'. */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Extra condition to gate whether the hint appears. Even if not yet
   * dismissed, the hint will only show when this is true. Default true.
   */
  when?: boolean;
  /** Called after the user dismisses (via X or Got it). Optional. */
  onDismiss?: () => void;
}

const STORAGE_PREFIX = 'vibe_hint_dismissed__';

/**
 * Returns true if the user has dismissed this hint in the past.
 */
export function isHintDismissed(id: string): boolean {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + id) === '1';
  } catch {
    return false;
  }
}

/**
 * Mark a hint as dismissed without rendering the component.
 * Useful when a user acts on the hint directly (e.g. right-clicks before
 * we get to show them the "right-click for more actions" hint).
 */
export function dismissHint(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + id, '1');
  } catch {
    // no-op in environments without localStorage
  }
}

/**
 * Dismissable coach mark that targets a DOM element. Renders once per
 * user per hint id (persisted to localStorage). Designed for discovery
 * moments where a non-obvious feature exists right next to where the
 * user is looking.
 */
export const CoachMark: React.FC<CoachMarkProps> = ({
  id,
  anchorRef,
  title,
  body,
  placement = 'bottom',
  when = true,
  onDismiss,
}) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  // Decide whether to show after mount. We wait a couple of frames so the
  // anchor has been laid out and we can measure it.
  useEffect(() => {
    if (!when) return;
    if (isHintDismissed(id)) return;

    let rafId = 0;
    let attempts = 0;
    const tryShow = () => {
      const el = anchorRef.current;
      if (!el) {
        if (attempts++ < 30) {
          rafId = requestAnimationFrame(tryShow);
        }
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        if (attempts++ < 30) {
          rafId = requestAnimationFrame(tryShow);
        }
        return;
      }
      setPos(computePosition(rect, placement));
      setVisible(true);
    };
    rafId = requestAnimationFrame(tryShow);
    return () => cancelAnimationFrame(rafId);
  }, [id, anchorRef, placement, when]);

  // Reposition on scroll / resize while visible
  useEffect(() => {
    if (!visible) return;
    const reposition = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos(computePosition(rect, placement));
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [visible, anchorRef, placement]);

  const handleDismiss = () => {
    dismissHint(id);
    setVisible(false);
    onDismiss?.();
  };

  if (!visible || !pos) return null;

  return (
    <div
      ref={bubbleRef}
      className="fixed z-[60] w-72 bg-navy text-white rounded-xl shadow-2xl border border-white/10 p-4 animate-fade-in-up"
      style={{
        top: pos.top,
        left: pos.left,
        // Keep the bubble inside the viewport if it would overflow on the right.
        maxWidth: 'calc(100vw - 16px)',
      }}
      role="dialog"
      aria-label={title}
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="text-xs font-semibold text-white leading-snug">{title}</h4>
          <p className="text-[11px] text-white/80 leading-relaxed mt-1">{body}</p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-0.5 rounded text-white/50 hover:text-white hover:bg-card/10 transition-colors flex-shrink-0"
          aria-label="Dismiss hint"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex justify-end mt-3">
        <button
          type="button"
          onClick={handleDismiss}
          className="text-[10px] font-medium text-white/90 hover:text-white bg-card/10 hover:bg-card/20 px-2.5 py-1 rounded-md transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

/**
 * Compute bubble position relative to an anchor's bounding rect.
 * Returns fixed-position coordinates. Bubble is 288px wide (w-72).
 */
function computePosition(
  rect: DOMRect,
  placement: 'top' | 'bottom' | 'left' | 'right'
): { top: number; left: number } {
  const BUBBLE_WIDTH = 288;
  const GAP = 12;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = 0;
  let left = 0;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
      break;
    case 'top':
      top = rect.top - GAP - 100; // bubble height is variable; 100 is a safe estimate
      left = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - 50;
      left = rect.right + GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - 50;
      left = rect.left - GAP - BUBBLE_WIDTH;
      break;
  }

  // Clamp to viewport
  left = Math.max(8, Math.min(left, viewportWidth - BUBBLE_WIDTH - 8));
  top = Math.max(8, Math.min(top, viewportHeight - 120));

  return { top, left };
}
